import { z } from 'zod';
import { ChallengeParamsSchema } from './challenge';

/**
 * REST request/response DTOs shared by the app and the API.
 * The server validates requests with these schemas; the app gets the types.
 */

// ---- Auth (phone + OTP via MSG91; no password, no email, KYC at payout) ----

export const OtpSendRequestSchema = z.object({
  /** National number, 10 digits, no country code. */
  phone: z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number'),
});
export type OtpSendRequest = z.infer<typeof OtpSendRequestSchema>;

export const OtpVerifyRequestSchema = z.object({
  phone: z.string().regex(/^[6-9]\d{9}$/),
  code: z.string().regex(/^\d{6}$/),
  /**
   * Affiliate/influencer code, applied only when this verify CREATES the
   * account (first-touch, permanent). Unknown or inactive codes are ignored
   * silently — a typo must never block a signup.
   */
  referralCode: z.string().trim().max(24).optional(),
});
export type OtpVerifyRequest = z.infer<typeof OtpVerifyRequestSchema>;

export const AuthSessionSchema = z.object({
  token: z.string(),
  user: z.object({
    id: z.string(),
    phone: z.string(),
    name: z.string().nullable(),
    handle: z.string().nullable(),
    kycVerified: z.boolean(),
    createdAt: z.number(),
  }),
});
export type AuthSession = z.infer<typeof AuthSessionSchema>;

/**
 * One live sign-in on one device, as its owner sees it.
 *
 * The same shape everywhere a person can be signed in — the web terminal and
 * the admin console — so "where am I signed in, and from where" reads
 * identically on both and one formatter serves them. `label` is built
 * server-side for exactly that reason: two clients inventing their own "Chrome
 * on Windows" from a user-agent string is two chances to describe the same
 * device differently.
 *
 * `ip` is the address the edge saw on the last request, not the one at
 * sign-in: someone checking this list wants to know where the session is being
 * used from NOW. There is no geolocation — we do not send an IP to a
 * third-party lookup to print a city we cannot vouch for.
 */
export const SessionInfoSchema = z.object({
  id: z.string(),
  /** The session making this request — never offer to kill it by surprise. */
  current: z.boolean(),
  /** Human-readable device summary, e.g. "Chrome on Windows". */
  label: z.string(),
  platform: z.string().nullable(),
  appVersion: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: z.number(),
  lastSeenAt: z.number(),
});
export type SessionInfo = z.infer<typeof SessionInfoSchema>;

export const SessionListSchema = z.object({ sessions: z.array(SessionInfoSchema) });
export type SessionList = z.infer<typeof SessionListSchema>;

// ---- Challenge join & payment (Razorpay) ----

export const JoinChallengeRequestSchema = z.object({
  challengeId: z.string(),
  /**
   * How this client pays. 'razorpay' (default) is the web checkout — the
   * server creates a Razorpay Order. 'iap' is the mobile apps: the stores own
   * the purchase, so the server only opens an enrollment shell and names the
   * store product; /payments/iap/verify closes the loop.
   */
  method: z.enum(['razorpay', 'iap']).default('razorpay'),
});
export type JoinChallengeRequest = z.infer<typeof JoinChallengeRequestSchema>;

/**
 * For 'razorpay': the server-created Order (amount in paise; key is the public
 * key_id — never the secret). For 'iap': razorpayOrderId/key carry the 'iap'
 * sentinel and productId names the store SKU to purchase. Free and dev joins
 * use the 'free'/'dev' sentinels and are already activated when this returns.
 */
export const JoinChallengeResponseSchema = z.object({
  enrollmentId: z.string(),
  razorpayOrderId: z.string(),
  amount: z.number(),
  currency: z.literal('INR'),
  key: z.string(),
  /** Store product id (App Store / Play, same SKU string) — 'iap' joins only. */
  productId: z.string().optional(),
});
export type JoinChallengeResponse = z.infer<typeof JoinChallengeResponseSchema>;

/**
 * App posts the store purchase here; the server verifies with Apple/Google
 * before activating. iOS sends the base64 app receipt, Android the purchase
 * token — exactly one of the two, matching `platform`.
 */
export const IapVerifyRequestSchema = z.object({
  enrollmentId: z.string(),
  platform: z.enum(['ios', 'android']),
  productId: z.string().min(1).max(128),
  /** iOS: base64 App Store receipt (react-native-iap transactionReceipt). */
  receipt: z.string().max(200_000).optional(),
  /** Android: Play Billing purchase token. */
  purchaseToken: z.string().max(4096).optional(),
});
export type IapVerifyRequest = z.infer<typeof IapVerifyRequestSchema>;

/** App posts checkout results here; server verifies the HMAC signature. */
export const PaymentVerifyRequestSchema = z.object({
  enrollmentId: z.string(),
  razorpayOrderId: z.string(),
  razorpayPaymentId: z.string(),
  razorpaySignature: z.string(),
});
export type PaymentVerifyRequest = z.infer<typeof PaymentVerifyRequestSchema>;

export const PaymentVerifyResponseSchema = z.object({
  status: z.enum(['paid', 'pending', 'failed']),
  enrollmentId: z.string(),
});
export type PaymentVerifyResponse = z.infer<typeof PaymentVerifyResponseSchema>;

// ---- Instrument draw (server-owned; the app only animates to the result) ----

export const DrawResponseSchema = z.object({
  enrollmentId: z.string(),
  /** The server-assigned instrument the reel decelerates to. */
  symbol: z.string(),
  name: z.string(),
  exchange: z.string(),
  kind: z.string(),
  /** Per-account randomized params, drawn server-side with the instrument. */
  params: ChallengeParamsSchema,
});
export type DrawResponse = z.infer<typeof DrawResponseSchema>;

// ---- Order placement (sim engine; server owns all fills) ----

export const PlaceOrderRequestSchema = z.object({
  symbol: z.string(),
  side: z.enum(['BUY', 'SELL']),
  orderType: z.enum(['Market', 'Limit', 'SL', 'SL-M']),
  product: z.enum(['MIS', 'CNC', 'NRML']).default('MIS'),
  // Upper bound is defense-in-depth: margin checks are the real gate, but no
  // legitimate paper order needs six-figure quantity.
  qty: z.number().int().positive().max(100_000),
  price: z.number().positive().optional(),
  triggerPrice: z.number().positive().optional(),
  /** Present when trading inside a challenge; absent in free practice. */
  enrollmentId: z.string().optional(),
});
export type PlaceOrderRequest = z.infer<typeof PlaceOrderRequestSchema>;

/**
 * Modify a resting (PENDING/TRIGGER) order in place — quantity, limit price,
 * trigger, any subset. The order keeps its identity and queue position in the
 * blotter; margin is re-checked against the modified terms exactly as if it
 * were being placed fresh.
 */
export const ModifyOrderRequestSchema = z
  .object({
    qty: z.number().int().positive().max(100_000).optional(),
    price: z.number().positive().optional(),
    triggerPrice: z.number().positive().optional(),
  })
  .refine((b) => b.qty != null || b.price != null || b.triggerPrice != null, {
    message: 'nothing to modify',
  });
export type ModifyOrderRequest = z.infer<typeof ModifyOrderRequestSchema>;

// ---- Payout wallet withdrawals (manual-approval; ops pays, then marks paid) ----

/** The linked bank account payouts are transferred to. One per user. */
export const BankAccountSchema = z.object({
  holder: z.string().trim().min(2, 'Name is required').max(80),
  bankName: z.string().trim().min(2, 'Bank name is required').max(80).optional(),
  accountNumber: z.string().regex(/^\d{9,18}$/, 'Enter a valid account number (9-18 digits)'),
  ifsc: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Enter a valid 11-character IFSC code'),
});
export type BankAccount = z.infer<typeof BankAccountSchema>;

/** What clients see: never the full account number. */
export const BankAccountInfoSchema = z.object({
  holder: z.string(),
  bankName: z.string().optional(),
  /** e.g. "••••3210". */
  accountMasked: z.string(),
  ifsc: z.string(),
});
export type BankAccountInfo = z.infer<typeof BankAccountInfoSchema>;

/**
 * Withdrawals pay to the linked bank account or directly submitted bank details.
 */
export const WithdrawRequestSchema = z.object({
  /** Whole rupees. */
  amount: z.number().int().min(500, 'Minimum withdrawal is ₹500').max(10_000_000),
  bank: BankAccountSchema.optional(),
});
export type WithdrawRequest = z.infer<typeof WithdrawRequestSchema>;

export const WithdrawalSchema = z.object({
  id: z.string(),
  amount: z.number(),
  status: z.enum(['pending', 'paid', 'rejected']),
  /**
   * Destination display string — bank account details ("HDFC Bank · John · ••3210 · HDFC0001234").
   */
  upi: z.string().nullable(),
  requestedAt: z.number(),
  decidedAt: z.number().nullable(),
  note: z.string().nullable(),
  estimatedHours: z.number().optional(),
});
export type Withdrawal = z.infer<typeof WithdrawalSchema>;

/**
 * App-wide runtime config, fetched at boot. Maintenance blocks the app with a
 * message; a client below minVersion gets a mandatory-update screen.
 */
export const AppConfigSchema = z.object({
  maintenance: z.object({ on: z.boolean(), message: z.string() }),
  /** Semver. Clients below this must update before continuing. */
  minVersion: z.string(),
  latestVersion: z.string(),
  /** Contact channels for the app's Help & Support screen; admin-editable. */
  support: z.object({
    email: z.string(),
    /** Optional phone/WhatsApp line, `+91…`; null hides the row. */
    phone: z.string().nullable(),
    /** Human-readable availability, e.g. "Mon–Fri · 9:00–18:00 IST". */
    hours: z.string().nullable(),
  }),
  depositBank: z
    .object({
      bankName: z.string(),
      accountHolder: z.string(),
      accountNumber: z.string(),
      ifsc: z.string(),
      upiId: z.string().optional(),
    })
    .optional(),
});
export type AppConfig = z.infer<typeof AppConfigSchema>;

export const ApiErrorSchema = z.object({
  error: z.string(),
  code: z.string().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;
